/**
 * Server-state hooks for the in-app inbox (C16 API) via TanStack Query.
 *
 * Mark-read and dismiss are *optimistic*: the cache is patched immediately and
 * rolled back on error. The unread badge in the app bar reads the same cached
 * {@link ListInboxResponse} so it always matches the grid.
 */
import { type InboxItem, type ListInboxResponse } from "@records-monitor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/api/useApiClient";
import { useUser } from "@/auth/AuthProvider";

export const inboxKey = ["notifications", "inbox"] as const;

/** Recompute the unread count from the current item set. */
function unreadOf(items: readonly InboxItem[]): number {
  return items.filter((i) => i.readAt === null && i.dismissedAt === null).length;
}

/**
 * GET /notifications/inbox — items + unread count. Only fires for an
 * authenticated user so the app-bar badge stays inert on the sign-in page.
 */
export function useInbox() {
  const api = useApiClient();
  const user = useUser();
  return useQuery({
    queryKey: inboxKey,
    queryFn: () => api.get<ListInboxResponse>("/notifications/inbox"),
    enabled: user !== null,
  });
}

/** Optimistically apply a patch to the cached inbox by item id. */
function patchItem(
  prev: ListInboxResponse | undefined,
  id: string,
  patch: Partial<InboxItem>,
): ListInboxResponse | undefined {
  if (!prev) return prev;
  const items = prev.items.map((item) => (item.id === id ? { ...item, ...patch } : item));
  return { items, unreadCount: unreadOf(items) };
}

/** POST /notifications/inbox/:id/read — optimistic mark-read. */
export function useMarkRead() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<InboxItem>(`/notifications/inbox/${id}/read`),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: inboxKey });
      const previous = queryClient.getQueryData<ListInboxResponse>(inboxKey);
      queryClient.setQueryData<ListInboxResponse>(inboxKey, (prev) =>
        patchItem(prev, id, { readAt: new Date().toISOString() }),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(inboxKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: inboxKey });
    },
  });
}

/** POST /notifications/inbox/:id/dismiss — optimistic dismiss. */
export function useDismiss() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<InboxItem>(`/notifications/inbox/${id}/dismiss`),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: inboxKey });
      const previous = queryClient.getQueryData<ListInboxResponse>(inboxKey);
      queryClient.setQueryData<ListInboxResponse>(inboxKey, (prev) =>
        patchItem(prev, id, { dismissedAt: new Date().toISOString() }),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(inboxKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: inboxKey });
    },
  });
}
